import React from "react";

import { briefPattern } from "./briefPatterns";
import {
  CLUSTER_PALETTE,
  type PortfolioClusterKey,
} from "./portfolioClusterStyles";
import { resolveSignalCopy } from "./signalCTAs";
import {
  isAlStale,
  type SignalCardAccountContext,
} from "./useSignalCardsWithAccountContext";
import CommonModal from "../../Components/Modal";
import { useAppSelector } from "../../Store/Hooks";
import type { SignalCard } from "../../Store/Reducers/SignalFeedSlice";
import { formatCompactCurrency } from "../../Utils/Helper/formatCompactCurrency";

/**
 * mock: renderBriefCard() (my-accounts-portfolio-v4.html lines 9364-9430).
 * Same sections in the same order: header (eyebrow, name, chips incl.
 * Pattern) → six tiles → divider → Suggested talking point → actions.
 * Differences from the mock, on purpose:
 *  - light header instead of the mock's near-black band, set off by a
 *    tinted background and a strong bottom border (Sushant, 2026-10-28);
 *  - no AL/RL trend arrows: no API returns a trend (the mock's RL arrow was
 *    derived from the level itself, not from history);
 *  - "Revenue" instead of "ARR": the figure is the account revenue that the
 *    Total Revenue tile also sums;
 *  - no pattern when AL or RL is unassessed (the mock read a missing level
 *    as "low" and showed a pattern anyway).
 */
export interface PreCallBriefTarget {
  card: SignalCard;
  context: SignalCardAccountContext;
}

interface PreCallBriefModalProps {
  target: PreCallBriefTarget | null;
  onClose: () => void;
  withOrg: (path: string) => string;
}

const BH_LABEL: Record<string, string> = {
  concern: "Concern",
  strong: "Strong",
  stable: "Stable",
  no_data: "No data",
};

const TIER_STYLES: Record<string, { label: string; chip: string }> = {
  critical: {
    label: "Critical",
    chip: "bg-red-100 text-red-700",
  },
  warning: {
    label: "Warning",
    chip: "bg-amber-100 text-amber-800",
  },
  opp: {
    label: "Opportunity",
    chip: "bg-teal-100 text-teal-800",
  },
  maint: {
    label: "Maintenance",
    chip: "bg-slate-100 text-slate-600",
  },
};

function MetricTile({
  label,
  value,
  sub,
  valueClassName = "text-gray-900",
}: {
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex min-h-[80px] flex-col rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
      <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-gray-400">
        {label}
      </div>
      <div
        className={`mt-1.5 text-[13.5px] font-bold leading-snug ${valueClassName}`}
      >
        {value}
      </div>
      {sub != null && sub !== "" ? (
        <div className="mt-auto pt-1.5 text-[11px] leading-snug text-gray-500">
          {sub}
        </div>
      ) : (
        <div
          className="mt-auto pt-1.5 text-[11px] text-transparent"
          aria-hidden
        >
          —
        </div>
      )}
    </div>
  );
}

function formatRenewal(days: number | null): {
  label: string;
  urgent: boolean;
  warn: boolean;
} {
  if (days == null) {
    return { label: "No date on file", urgent: false, warn: false };
  }
  if (days < 0) {
    return {
      label: `${Math.abs(days)} days overdue`,
      urgent: true,
      warn: false,
    };
  }
  if (days < 60) {
    return { label: `${days} days`, urgent: true, warn: false };
  }
  if (days < 120) {
    return { label: `${days} days`, urgent: false, warn: true };
  }
  if (days < 180) {
    return { label: `${days} days`, urgent: false, warn: false };
  }
  return { label: "No near-term renewal", urgent: false, warn: false };
}

function Chip({
  children,
  className = "bg-gray-100 text-gray-700",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`rounded-full px-2.5 py-[3px] text-[11px] font-semibold ${className}`}
    >
      {children}
    </span>
  );
}

const PreCallBriefModal: React.FC<PreCallBriefModalProps> = ({
  target,
  onClose,
  withOrg,
}) => {
  const parameters = useAppSelector(state => state.signalFeed.parameters);
  if (!target) return null;
  const { card, context } = target;
  const copy = resolveSignalCopy(
    card.signal_key,
    card.evidence?.triggeredFields,
    card.cta?.card_copy,
    parameters
  );
  const pattern = briefPattern(context.al, context.rl);
  const renewal = formatRenewal(context.renewalDays);
  const alStr = context.al != null ? `AL ${context.al}` : "Not assessed";
  const rlStr = context.rl != null ? `RL ${context.rl}` : "Not assessed";
  const bhLabel = context.bhClass ? BH_LABEL[context.bhClass] : "No data";
  const bhStr = context.bh != null ? `${bhLabel} (${context.bh})` : bhLabel;
  const staleDaysStr =
    context.al == null
      ? "No assessment on file"
      : context.alStaleDays == null
        ? "Date unknown"
        : context.alStaleDays === 0
          ? "Today"
          : `${context.alStaleDays} days ago`;
  const clusterKey = context.cluster as PortfolioClusterKey | null;
  const clusterPalette =
    clusterKey && clusterKey !== "unscored"
      ? CLUSTER_PALETTE[clusterKey]
      : null;
  const clusterLabel = clusterPalette ? clusterPalette.label : "Unscored";
  const bhConcern = context.bhClass === "concern";
  const ahMissing = context.ah == null || context.ah <= 0;
  const ahLow = !ahMissing && context.ah! < 50;
  const tierStyle = TIER_STYLES[card.tier] ?? TIER_STYLES.maint;
  const showRenewalChip =
    context.renewalDays != null && context.renewalDays < 180;
  const renewalChipUrgent =
    context.renewalDays != null && context.renewalDays < 60;
  const revenueStr = formatCompactCurrency(context.revenue, {
    fallback: "—",
  });

  return (
    <CommonModal
      isOpen
      toggle={onClose}
      onCloseButton={onClose}
      showHeader={false}
      usePortal
      size="2xl"
      className="!max-w-[600px] !rounded-[14px] overflow-hidden shadow-2xl"
      contentClassName="!p-0 overflow-hidden"
    >
      {/* .brief-header — light version of the mock's dark band */}
      <div className="border-b-2 border-purple-200 bg-purple-50/70 px-[22px] pb-4 pt-5">
        <div className="mb-1 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.1em] text-purple-700/70">
          Pre-call Brief
          <button
            type="button"
            onClick={onClose}
            title="Close"
            className="ml-auto inline-flex h-7 w-7 items-center justify-center rounded-full text-lg leading-none text-gray-400 transition hover:bg-white hover:text-gray-700"
          >
            ✕
          </button>
        </div>
        <h2 className="mb-2.5 truncate text-[22px] font-extrabold leading-tight text-gray-900">
          {card.account_name}
        </h2>
        <div className="flex flex-wrap gap-1.5">
          {clusterPalette && (
            <Chip className="bg-white text-gray-800 ring-1 ring-gray-200">
              {clusterPalette.label}
            </Chip>
          )}
          <Chip>{alStr === "Not assessed" ? "AL not assessed" : alStr}</Chip>
          <Chip>{rlStr === "Not assessed" ? "RL not assessed" : rlStr}</Chip>
          {showRenewalChip && context.renewalDays != null && (
            <Chip
              className={
                renewalChipUrgent
                  ? "bg-red-100 text-red-700"
                  : "bg-gray-100 text-gray-700"
              }
            >
              {context.renewalDays < 0
                ? `Renewal overdue: ${Math.abs(context.renewalDays)}d`
                : `Renewal: ${context.renewalDays}d`}
            </Chip>
          )}
          {bhConcern && (
            <Chip className="bg-amber-100 text-amber-800">
              BizHealth: Concern
            </Chip>
          )}
          {pattern && (
            <Chip className="bg-purple-200/70 text-purple-800">
              Pattern: {pattern.name}
            </Chip>
          )}
        </div>
      </div>

      {/* .brief-body */}
      <div className="bg-white px-[22px] py-[18px]">
        <div className="grid grid-cols-3 gap-3">
          <MetricTile
            label="AL level"
            value={alStr}
            valueClassName={
              context.al != null && isAlStale(context.alStaleDays, parameters)
                ? "text-amber-600"
                : context.al == null
                  ? "text-gray-400"
                  : "text-gray-900"
            }
            sub={`Last assessed: ${staleDaysStr}`}
          />
          <MetricTile
            label="RL level"
            value={rlStr}
            valueClassName={
              context.rl == null ? "text-gray-400" : "text-gray-900"
            }
            sub={`Pattern: ${pattern ? pattern.name : "—"}`}
          />
          <MetricTile
            label="Renewal"
            value={renewal.label}
            valueClassName={
              renewal.urgent
                ? "text-red-600"
                : renewal.warn
                  ? "text-amber-600"
                  : "text-gray-900"
            }
            sub={`Revenue: ${revenueStr}`}
          />
          <MetricTile
            label="Business Health"
            value={bhStr}
            valueClassName={bhConcern ? "text-amber-600" : "text-gray-900"}
            sub={
              bhConcern
                ? "External concern signals"
                : "No external concern signals"
            }
          />
          <MetricTile
            label="Account Health"
            value={
              ahMissing || context.ah == null
                ? "—"
                : String(Math.round(context.ah))
            }
            valueClassName={ahLow ? "text-red-600" : "text-gray-900"}
            sub={`Cluster: ${clusterLabel}`}
          />
          <MetricTile
            label="Active signal"
            value={copy.title}
            valueClassName="text-gray-900"
            sub={tierStyle.label}
          />
        </div>

        <hr className="my-3.5 border-gray-200" />

        {/* .brief-talking-point */}
        <div className="mb-3.5 rounded-lg border border-purple-200 bg-purple-50/50 px-4 py-3.5">
          <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.07em] text-purple-700">
            Suggested talking point
            {pattern ? ` — ${pattern.name}` : ""}
          </div>
          <p className="m-0 text-[13px] italic leading-relaxed text-gray-900">
            {pattern
              ? pattern.talking
              : `No talking point yet: ${
                  context.al == null && context.rl == null
                    ? "AL and RL have"
                    : context.al == null
                      ? "AL has"
                      : "RL has"
                } not been assessed for this account, so it has no AL × RL pattern.`}
          </p>
        </div>

        {/* .brief-close-row */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() =>
              window.location.assign(
                withOrg(`/account-synopsis/${card.account_id}`)
              )
            }
            className="inline-flex items-center rounded-lg bg-brand px-3.5 py-2 text-[12.5px] font-semibold text-white shadow-sm hover:bg-brand-dark"
          >
            Open full synopsis
          </button>
          <button
            type="button"
            onClick={onClose}
            className="ml-auto rounded-lg px-2 py-2 text-[12.5px] font-medium text-gray-400 hover:bg-gray-50 hover:text-gray-700"
          >
            Dismiss brief
          </button>
        </div>
      </div>
    </CommonModal>
  );
};

export default PreCallBriefModal;
